import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components-new/button';
import { Input } from '@/components-new/input';
import { Select } from '@/components-new/select';
import {
  defaultNotificationHistoryFilters,
  NotificationHistoryFilterData
} from '@/features/notification-history/types/notification-history-filter-data';

type NotificationHistoryFiltersProps = {
  defaultValue: NotificationHistoryFilterData;
  onApplyFilters: (data: NotificationHistoryFilterData) => void;
};

export const NotificationHistoryFilters = ({ defaultValue, onApplyFilters }: NotificationHistoryFiltersProps) => {
  const { control, handleSubmit, watch, reset } = useForm<NotificationHistoryFilterData>({
    defaultValues: defaultValue
  });

  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const subscription = watch(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => formRef.current?.requestSubmit(), 300);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [watch]);

  const onSubmit = handleSubmit((data) => onApplyFilters(data));

  const onReset = () => {
    reset(defaultNotificationHistoryFilters);
    onApplyFilters(defaultNotificationHistoryFilters);
  };

  return (
    <form ref={formRef} onSubmit={onSubmit} onReset={(e) => { e.preventDefault(); onReset(); }}>
      <div className="mb-4 border-b border-zinc-200 pb-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-56">
            <Controller
              control={control}
              name="recipient"
              render={({ field }) => (
                <Input {...field} type="search" placeholder="Search by recipient" aria-label="Search by recipient"/>
              )}
            />
          </div>
          <div className="w-56">
            <Controller
              control={control}
              name="subject"
              render={({ field }) => (
                <Input {...field} type="search" placeholder="Search by subject" aria-label="Search by subject"/>
              )}
            />
          </div>
          <div className="w-40">
            <Controller
              control={control}
              name="channel"
              render={({ field }) => (
                <Select {...field} aria-label="Channel">
                  <option value="all">All Channels</option>
                  <option value="Email">Email</option>
                  <option value="Push">Push</option>
                  <option value="InApp">In-App</option>
                </Select>
              )}
            />
          </div>
          <div className="w-40">
            <Controller
              control={control}
              name="sentFrom"
              render={({ field }) => (
                <Input {...field} type="date" aria-label="Sent from"/>
              )}
            />
          </div>
          <div className="w-40">
            <Controller
              control={control}
              name="sentTo"
              render={({ field }) => (
                <Input {...field} type="date" aria-label="Sent to"/>
              )}
            />
          </div>
          <Button outline type="reset" aria-label="Clear filters" className="shrink-0">Clear</Button>
        </div>
      </div>
    </form>
  );
};
